// Detailseite eines Kurseinstellungs-Eintrags.
//
// - Stammdaten (Titel, Geltung, Autor) und die Markdown-Beschreibung
//   werden per PUT als Ganzes zurückgeschrieben.
// - Der Status bleibt separat im Header (Inline-PATCH, sofortige Wirkung)
//   – analog zur Ereignis-Detailseite.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api.js';
import MarkdownEditor from './MarkdownEditor.jsx';
import ScopeSelect, { formatScope } from './ScopeSelect.jsx';
import StatusBadge from './StatusBadge.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

const EMPTY_FORM = {
    title: '',
    description: '',
    author: '',
    mandant_id: null,
    course_set: null,
};

export default function CourseSettingEventDetail() {
    const { id: idParam } = useParams();
    const navigate = useNavigate();
    const id = Number(idParam);

    const [data, setData] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [mandanten, setMandanten] = useState([]);
    const [courseSets, setCourseSets] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoadError('');
            const d = await api.getCourseSettingEvent(id);
            setData(d);
            setForm({
                title: d.title ?? '',
                description: d.description ?? '',
                author: d.author ?? '',
                mandant_id: d.mandant_id ?? null,
                course_set: d.course_set ?? null,
            });
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        api.listMandanten().then(setMandanten).catch(() => { /* unkritisch */ });
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
        api.listUserOptions().then(setUserOptions).catch(() => { /* unkritisch */ });
    }, [load]);

    function updateField(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
        setSavedNotice(false);
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        setSavedNotice(false);
        try {
            const updated = await api.updateCourseSettingEvent(id, {
                title: form.title,
                description: form.description,
                author: form.author,
                mandant_id: form.mandant_id,
                course_set: form.course_set,
            });
            setData(updated);
            setSavedNotice(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function handleStatusChange(status) {
        try {
            const updated = await api.updateCourseSettingEventStatus(id, status);
            setData(updated);
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDelete() {
        if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
        try {
            await api.deleteCourseSettingEvent(id);
            navigate('/kurseinstellungen');
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;
    if (loadError) {
        return (
            <div>
                <Link to="/kurseinstellungen">← Zurück zur Übersicht</Link>
                <div className="panel" style={{ color: 'var(--color-danger)', marginTop: 12 }}>
                    Fehler: {loadError}
                </div>
            </div>
        );
    }

    return (
        <div className="page-centered page-centered--wide">
            <div style={{ marginBottom: 8 }}>
                <Link to="/kurseinstellungen">← Zurück zur Übersicht</Link>
            </div>

            <div className="toolbar">
                <h1 style={{ margin: 0 }}>{data.title}</h1>
                <StatusBadge status={data.status} />
                <span className="muted">
                    {data.mandant_id || data.course_set
                        ? formatScope(data)
                        : 'alle Mandanten'}
                </span>
                <div className="spacer" />
                <select
                    className="status-select"
                    value={data.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    title="Status sofort ändern"
                >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className="btn btn--danger" onClick={handleDelete}>
                    Eintrag löschen
                </button>
            </div>

            <form className="panel" onSubmit={handleSave}>
                <h2>Bearbeiten</h2>
                {saveError && <div className="login__error">{saveError}</div>}
                {savedNotice && (
                    <div
                        className="panel"
                        style={{ color: 'var(--color-success)', marginBottom: 12, padding: 8 }}
                    >
                        Gespeichert.
                    </div>
                )}

                <div className="field">
                    <label>Titel *</label>
                    <input
                        type="text"
                        required
                        value={form.title}
                        onChange={(e) => updateField('title', e.target.value)}
                    />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Geltung</label>
                        <ScopeSelect
                            value={{ mandant_id: form.mandant_id, course_set: form.course_set }}
                            onChange={(next) => {
                                setForm((f) => ({ ...f, ...next }));
                                setSavedNotice(false);
                            }}
                            mandanten={mandanten}
                            courseSets={courseSets}
                        />
                    </div>
                    <div className="field">
                        <label>Autor</label>
                        <select
                            value={form.author}
                            onChange={(e) => updateField('author', e.target.value)}
                        >
                            <option value="">– (kein Autor) –</option>
                            {userOptions.map((u) => (
                                <option key={u.id} value={u.username}>{u.username}</option>
                            ))}
                            {/* Autor beibehalten, falls der User nicht mehr existiert. */}
                            {form.author && !userOptions.some((u) => u.username === form.author) && (
                                <option value={form.author}>{form.author}</option>
                            )}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>Beschreibung (Markdown)</label>
                    <MarkdownEditor
                        value={form.description}
                        onChange={(md) => updateField('description', md)}
                        height="420px"
                    />
                </div>

                <div className="muted" style={{ fontSize: 12, margin: '12px 0' }}>
                    Angelegt am {formatDate(data.created_at)}
                    {data.updated_at && ` · zuletzt geändert am ${formatDate(data.updated_at)}`}
                </div>

                <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? 'Speichere…' : 'Speichern'}
                </button>
            </form>
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
